import {
  COST_CHANGE_SOURCE_LABELS,
  type ItemCostHistoryEntry,
  type Product,
  type ProductPriceHistoryEntry,
  type RawMaterial,
} from "@artisancode/api-types";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useProductCostHistory,
  useProductPriceHistory,
  useRawMaterialCostHistory,
} from "@/hooks/use-item-history";

import { rawMaterialUomLabel } from "./raw-material-utils";

const formatDateTime = (iso: string) =>
  new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Jakarta",
  }).format(new Date(iso));

// Costs are per base unit (g / mL), so they can be fractions of a rupiah.
const formatValue = (n: number | null) =>
  n == null
    ? "-"
    : `Rp ${n.toLocaleString("id-ID", { maximumFractionDigits: 4 })}`;

interface Row {
  id: string;
  at: string;
  before: number | null;
  after: number;
  source: string;
  by: string | null;
}

function HistoryTable({
  title,
  rows,
  loading,
}: {
  title: string;
  rows: Row[];
  loading: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <h4 className="text-xs font-semibold text-foreground">{title}</h4>
      {loading ? (
        <Skeleton className="h-16 w-full" />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
          Belum ada perubahan tercatat.
        </p>
      ) : (
        <div className="max-h-64 overflow-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="h-8 text-[11px]">Waktu</TableHead>
                <TableHead className="h-8 text-right text-[11px]">
                  Sebelum
                </TableHead>
                <TableHead className="h-8 text-right text-[11px]">
                  Sesudah
                </TableHead>
                <TableHead className="h-8 text-[11px]">Sumber</TableHead>
                <TableHead className="h-8 text-[11px]">Oleh</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="py-1.5 text-xs whitespace-nowrap">
                    {formatDateTime(r.at)}
                  </TableCell>
                  <TableCell className="py-1.5 text-right text-xs text-muted-foreground">
                    {formatValue(r.before)}
                  </TableCell>
                  <TableCell className="py-1.5 text-right text-xs font-medium">
                    {formatValue(r.after)}
                  </TableCell>
                  <TableCell className="py-1.5 text-xs">{r.source}</TableCell>
                  <TableCell className="py-1.5 text-xs text-muted-foreground">
                    {r.by ?? "-"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

const fromCost = (e: ItemCostHistoryEntry): Row => ({
  id: e.id,
  at: e.createdAt,
  before: e.costBefore,
  after: e.costAfter,
  source:
    e.sourceLabel ?? COST_CHANGE_SOURCE_LABELS[e.sourceType] ?? e.sourceType,
  by: e.createdByName,
});

const fromPrice = (e: ProductPriceHistoryEntry): Row => ({
  id: e.id,
  at: e.createdAt,
  before: e.priceBefore,
  after: e.priceAfter,
  source: e.priceBefore == null ? "Harga awal" : "Diubah manual",
  by: e.createdByName,
});

interface DialogProps<T> {
  item: T | null;
  onOpenChange: (open: boolean) => void;
}

export function ProductHistoryDialog({
  item,
  onOpenChange,
}: DialogProps<Product>) {
  const price = useProductPriceHistory(item?.id ?? null);
  const cost = useProductCostHistory(item?.id ?? null);
  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Riwayat Harga: {item?.name}</DialogTitle>
          <DialogDescription>
            Perubahan harga jual dan HPP, terbaru di atas.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <HistoryTable
            title="Harga Jual"
            rows={(price.data?.items ?? []).map(fromPrice)}
            loading={price.isLoading}
          />
          <HistoryTable
            title="COGS / HPP"
            rows={(cost.data?.items ?? []).map(fromCost)}
            loading={cost.isLoading}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RawMaterialHistoryDialog({
  item,
  onOpenChange,
}: DialogProps<RawMaterial>) {
  const cost = useRawMaterialCostHistory(item?.id ?? null);
  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Riwayat Harga: {item?.name}</DialogTitle>
          <DialogDescription>
            Perubahan harga rata-rata per{" "}
            {(item && rawMaterialUomLabel(item)) ?? "satuan"}, terbaru di atas.
          </DialogDescription>
        </DialogHeader>
        <HistoryTable
          title="Harga Rata-rata"
          rows={(cost.data?.items ?? []).map(fromCost)}
          loading={cost.isLoading}
        />
      </DialogContent>
    </Dialog>
  );
}
